import { useNotifications } from '../../../hooks/useNotifications';
import {
  formatTimeAgo,
  getNotificationIconType,
  groupNotificationsByPeriod,
} from '../../../utils/notificationHelpers';

function CloseIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function NotificationIcon({ type }) {
  const icons = {
    chapter: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <rect x="8" y="6" width="24" height="32" rx="3" fill="#4DA3FF" />
        <path d="M14 16h14M14 22h14M14 28h10" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="34" cy="34" r="10" fill="#FFC107" />
        <path d="M34 28v8M30 32h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    progress: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <rect x="6" y="14" width="36" height="20" rx="4" fill="#E8F4FF" stroke="#4DA3FF" strokeWidth="2" />
        <rect x="10" y="18" width="10" height="12" rx="2" fill="#FF6B6B" />
        <rect x="22" y="18" width="8" height="12" rx="2" fill="#FFC107" />
        <rect x="32" y="18" width="6" height="12" rx="2" fill="#4CAF50" />
      </svg>
    ),
    offer: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <rect x="10" y="16" width="28" height="22" rx="3" fill="#FF5A5A" />
        <path d="M10 22h28" stroke="#FFC107" strokeWidth="3" />
        <rect x="18" y="10" width="12" height="8" rx="2" fill="#FFC107" />
      </svg>
    ),
    download: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <path d="M10 14h28v24H10z" fill="#FFD966" stroke="#E6B800" strokeWidth="2" rx="2" />
        <circle cx="34" cy="34" r="10" fill="#4DA3FF" />
        <path d="M34 29v10M29 34h10" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    report: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <rect x="10" y="8" width="24" height="32" rx="3" fill="#FFF" stroke="#4DA3FF" strokeWidth="2" />
        <path d="M16 18h12M16 24h12M16 30h8" stroke="#4DA3FF" strokeWidth="2" strokeLinecap="round" />
        <circle cx="36" cy="34" r="8" fill="#7BC67E" />
        <path d="M33 34l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    default: (
      <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden>
        <circle cx="24" cy="24" r="18" fill="#E8F4FF" stroke="#4DA3FF" strokeWidth="2" />
        <path d="M24 16v10l6 4" stroke="#4DA3FF" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  };

  return icons[type] || icons.default;
}

function NotificationCard({ notification }) {
  const iconType = getNotificationIconType(notification.title, notification.body);

  return (
    <article className="flex gap-3 rounded-xl border border-[#7FB3E0] bg-[#D1E5F7] p-3">
      <div className="shrink-0">
        <NotificationIcon type={iconType} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-bold text-[#1B1B1B]">{notification.title}</h4>
          <span className="shrink-0 text-xs text-[#666666]">{formatTimeAgo(notification.created_at)}</span>
        </div>
        <p className="mt-1 text-sm leading-snug text-[#666666]">{notification.body}</p>
      </div>
    </article>
  );
}

function NotificationSection({ title, items }) {
  if (!items.length) return null;

  return (
    <section className="mt-5">
      <h3 className="text-base font-bold text-[#1B1B1B]">{title}</h3>
      <ul className="mt-3 space-y-3">
        {items.map((notification) => (
          <li key={notification.id}>
            <NotificationCard notification={notification} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function NotificationPanel({ open, onClose }) {
  const { notifications, loading, error } = useNotifications({ enabled: open });
  const { today, lastWeek } = groupNotificationsByPeriod(notifications);
  const todayCount = today.length;

  if (!open) return null;

  return (
    <div
      className="absolute right-0 top-full z-50 mt-3 w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.15)]"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="relative border-b border-gray-100 px-5 pb-4 pt-5">
        <h2 className="text-center text-xl font-semibold text-[#9369CC]">Notification</h2>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-[#666666] transition hover:bg-gray-50"
          aria-label="Close notifications"
        >
          <CloseIcon />
        </button>
        <p className="mt-3 text-center text-sm text-[#666666]">
          You have{' '}
          <span className="font-semibold text-[#1A87CC]">
            {todayCount} notification{todayCount === 1 ? '' : 's'}
          </span>{' '}
          today
        </p>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-5 pb-5">
        {loading ? (
          <div className="space-y-3 py-4">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-20 animate-pulse rounded-xl bg-[#D1E5F7]/60" />
            ))}
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">{error}</p>
        ) : notifications.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#666666]">No notifications yet.</p>
        ) : (
          <>
            <NotificationSection title="Today" items={today} />
            <NotificationSection title="Last Week" items={lastWeek} />
            {!today.length && !lastWeek.length && (
              <p className="py-8 text-center text-sm text-[#666666]">No recent notifications.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default NotificationPanel;
